import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Drawer,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { settings } from "../data/settings";
import { RDVButton } from "./commonWidgets";

// Barre de navigation avec couleur dynamique
const StyledAppBar = styled(AppBar)`
  height: ${settings.navbar.height} !important;
  background-color: ${(props) => props.bgColor || "#1976d2"} !important;
  color: ${(props) => props.fontColor || "black"} !important;
`;

// Conteneur flexible pour les sections
const LeftContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  height: 100%;
`;

const CenterContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: ${(props) =>
    props.position === "center" ? "center" : "flex-start"};
  height: 100%;
`;

const RightContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: flex-end;
  height: 100%;
`;

// Logo stylisé
const Logo = styled.div`
  margin: auto;
  ${(props) => (props.isDrawer === true ? "width: 60%;" : "height: 80%;")};
  margin-left: ${(props) => (props.position === "right" ? "auto" : "")};
  margin-right: ${(props) => (props.position === "left" ? "auto" : "")};
  & img {
    ${(props) => (props.isDrawer === true ? "width: 100%;" : "height: 100%;")};
  }
`;

// Styles des éléments de menu
const StyledListItem = styled(ListItem)`
  text-transform: uppercase !important;
  color: white;
  text-decoration: none;
  white-space: nowrap;
  font-weight: bold !important;
  & :hover {
    font-weight: bold !important;
  }
`;
const StyledListItemText = styled(ListItemText)`
  text-transform: uppercase !important;
  color: white;
  text-decoration: none;
  white-space: nowrap;
  font-weight: bold !important;
  & span {
    font-weight: ${(props) =>
      props.active === true ? "bold" : "normal"}; !important;
  }
`;

const RdvButtonContainer = styled.div`
  margin-left: 1rem;
`;

const RdvButtonDrawerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  margin-top: 1rem;
`;

const Navbar = ({ pages, settings }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation(); // Récupérer la page actuelle

  const [firstKeyPage, setFirstKeyPage] = useState(
    pages && Object.keys(pages).length > 0 ? Object.keys(pages)[0] : ""
  );

  const toggleDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setFirstKeyPage(
      pages && Object.keys(pages).length > 0 ? Object.keys(pages)[0] : ""
    );
  }, [pages]);

  return (
    <>
      <StyledAppBar
        position="fixed"
        bgColor={settings?.navbar?.backgroundColor}
        fontColor={settings?.navbar?.fontColor}
      >
        <Toolbar>
          {/* Zone Gauche */}
          <LeftContainer>
            {/* Menu burger en mobile */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Liens qui doivent être à gauche */}
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              {Object.keys(pages)
                .filter((key) => pages[key].position === "left")
                .map((key, index) => (
                  <StyledListItem
                    key={key}
                    component={Link}
                    to={pages[key].path ?? `/${key}`}
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <StyledListItemText
                      primary={pages[key].title}
                      active={
                        location.pathname === `/${key}` ||
                        (location.pathname === "/" && key === firstKeyPage)
                      }
                    />
                  </StyledListItem>
                ))}
            </Box>
          </LeftContainer>

          {/* Zone Centre */}
          <CenterContainer position={settings.navbar?.logoPosition}>
            {settings.navbar?.logoImg && (
              <Logo position={settings.navbar?.logoPosition}>
                {settings.navbar?.logoImg}
              </Logo>
            )}
          </CenterContainer>
          {/* Zone Droite */}
          <RightContainer>
            <Box
              sx={{
                display: { xs: "none", md: "flex", alignItems: "center" },
              }}
            >
              {Object.keys(pages)
                .filter((key) => pages[key].position === "right")
                .map((key, index) => (
                  <StyledListItem
                    key={key}
                    component={Link}
                    to={pages[key].path ?? `/${key}`}
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <StyledListItemText
                      primary={pages[key].title}
                      active={
                        location.pathname === `/${key}` ||
                        (location.pathname === "/" && key === firstKeyPage)
                      }
                    />
                  </StyledListItem>
                ))}
              <RdvButtonContainer>
                <RDVButton invertColor={true} />
              </RdvButtonContainer>
            </Box>
          </RightContainer>
        </Toolbar>
      </StyledAppBar>

      {/* Menu mobile (Drawer) */}
      <Drawer anchor="left" open={mobileOpen} onClose={toggleDrawer}>
        <List
          sx={{
            height: "100%",
            width: 250,
            backgroundColor: settings?.navbar?.backgroundColor, // Utilise la même couleur que l'AppBar
            color: settings?.navbar?.fontColor || "black", // Utilise la même couleur pour le texte
          }}
        >
          {/* Logo en haut du menu mobile */}
          {settings.navbar?.logoImg && (
            <Logo isDrawer={true}>{settings.navbar?.logoImg}</Logo>
          )}
          <RdvButtonDrawerContainer>
            <RDVButton invertColor={true} />
          </RdvButtonDrawerContainer>

          {Object.keys(pages).map((pageKey, index) => (
            <StyledListItem
              button
              key={pageKey}
              component={Link}
              to={pages[pageKey].path ?? `/${pageKey}`}
              onClick={toggleDrawer}
            >
              <StyledListItemText
                primary={pages[pageKey].title}
                active={
                  location.pathname === `/${pageKey}` ||
                  (location.pathname === "/" && pageKey === firstKeyPage)
                }
              />
            </StyledListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
